import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, imgUrl } from "../lib/api";
import { useLang } from "../i18n";

export default function Courses() {
  const { t, lang } = useLang();
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", participants: 1, notes: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const openBooking = (course) => {
    setSelected(course);
    setForm({ name: "", email: "", phone: "", date: course.next_dates?.[0] || "", participants: 1, notes: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/courses/book", { course_id: selected.id, ...form, participants: Number(form.participants) });
      toast.success(t("courses.form.success"));
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="courses-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight" data-testid="courses-title">{t("courses.title")}</h1>
        <p className="mt-4 text-cocoa/60 max-w-xl">{t("courses.sub")}</p>
      </motion.div>
      <div className="mt-14 space-y-10">
        {courses.map((c, i) => (
          <motion.article key={c.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
            data-testid={`course-card-${c.id}`}>
            <div className="relative overflow-hidden rounded-sm border border-[#E5D9C8] group">
              <img src={imgUrl(c.image)} alt={c.title_it} className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-4 left-4 bg-parchment/90 backdrop-blur px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest text-amber-800">{c.level}</span>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-800">{c.duration}</p>
              <h2 className="mt-3 font-serif text-3xl font-semibold leading-snug">{lang === "it" ? c.title_it : c.title_en}</h2>
              <p className="mt-4 text-cocoa/70 leading-relaxed">{lang === "it" ? c.desc_it : c.desc_en}</p>
              <div className="mt-5 flex flex-wrap gap-2" data-testid={`course-dates-${c.id}`}>
                {(c.next_dates || []).map((d) => (
                  <span key={d} className="flex items-center gap-1.5 bg-cream border border-[#E5D9C8] px-3 py-1.5 text-xs font-mono text-cocoa/70">
                    <CalendarDays size={12} className="text-honey" /> {d}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-sm text-cocoa/60"><span className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50">{t("courses.includes")}:</span> {lang === "it" ? c.includes_it : c.includes_en}</p>
              <div className="mt-7 flex items-center gap-6">
                <p className="font-mono text-2xl font-semibold text-honey" data-testid={`course-price-${c.id}`}>€{c.price.toFixed(0)} <span className="text-sm text-cocoa/50">/ {lang === "it" ? "persona" : "person"}</span></p>
                <button onClick={() => openBooking(c)} data-testid={`course-book-btn-${c.id}`}
                  className="bg-bark text-parchment px-7 py-3.5 font-medium hover:bg-honey transition-colors duration-300">
                  {t("courses.book")}
                </button>
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelected(null)}
              className="fixed inset-0 bg-bark/60 backdrop-blur-sm z-50" />
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.97 }}
              className="fixed inset-x-4 top-[8%] sm:inset-x-0 sm:mx-auto max-w-lg bg-parchment z-50 rounded-sm border border-[#E5D9C8] shadow-2xl max-h-[85vh] overflow-y-auto"
              data-testid="booking-modal">
              <div className="flex items-center justify-between px-7 py-5 border-b border-[#E5D9C8]">
                <h3 className="font-serif text-2xl font-semibold">{t("courses.form.title")}</h3>
                <button onClick={() => setSelected(null)} data-testid="booking-close-btn" className="p-1.5 hover:text-honey transition-colors"><X size={19} /></button>
              </div>
              <form onSubmit={submit} className="px-7 py-6 space-y-4">
                <p className="text-sm text-cocoa/60 font-medium">{lang === "it" ? selected.title_it : selected.title_en}</p>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("checkout.name")}
                  data-testid="booking-name-input" className="w-full bg-white border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("checkout.email")}
                  data-testid="booking-email-input" className="w-full bg-white border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("checkout.phone")}
                  data-testid="booking-phone-input" className="w-full bg-white border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-cocoa/50 mb-1.5">{t("courses.form.date")}</label>
                    <select value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="booking-date-select"
                      className="w-full bg-white border border-[#E5D9C8] px-3 py-3 text-sm focus:outline-none focus:border-honey">
                      {(selected.next_dates || []).map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-cocoa/50 mb-1.5">{t("courses.form.participants")}</label>
                    <div className="flex items-center gap-2 border border-[#E5D9C8] bg-white px-3 py-3">
                      <Users size={14} className="text-honey" />
                      <input type="number" min="1" max="20" value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })}
                        data-testid="booking-participants-input" className="w-full text-sm focus:outline-none" />
                    </div>
                  </div>
                </div>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("courses.form.notes")} rows={3}
                  data-testid="booking-notes-input" className="w-full bg-white border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
                <button type="submit" disabled={sending} data-testid="booking-submit-btn"
                  className="w-full bg-bark text-parchment py-4 font-medium hover:bg-honey transition-colors duration-300 disabled:opacity-60">
                  {sending ? t("common.loading") : t("courses.form.submit")}
                </button>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
